import { ChangeDetectionStrategy, DestroyRef, Component, OnInit, signal, input } from '@angular/core';
import { SortColumn } from '../models/sortColumn.model';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { InLineControlRulesTableColumns } from '../models/tableColumns/inLineControlRulesTableColumns.model';
import {
  EdgeRule,
  InLineControlRulesViewModel,
  EdgeRuleAction,
  EdgeRulePolicy,
} from '../models/viewModels/InLineControlRulesViewModel';
import { PoliciesInLineControlRulesHttpService } from '../services/httpServices/policesInLineControlRulesHttpService/polices-in-line-control-rules-http.service';
import { ITablePageInfo } from 'src/app/models/componentInputs/tablePageInfo.interface';
import { ToggleClicked } from '../components/table-row/table-row.component';
import { StatusLabelEnum } from '../components/status-label/status-label.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-in-line-control-rules',
  templateUrl: './in-line-control-rules.component.html',
  styleUrl: './in-line-control-rules.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InLineControlRulesComponent implements OnInit {
  public readonly integrationId = input.required<string>();
  public isLoading = signal<boolean>(false);
  public sortedColumn = signal<SortColumn | undefined>(undefined);
  public tableColumns = InLineControlRulesTableColumns;
  public inLineControlRulesCurrentPage$ = new BehaviorSubject<InLineControlRulesViewModel[]>([]);
  public tableRows$: Observable<BehaviorSubject<InLineControlRulesViewModel>[]>;
  public inLineControlRulesData: EdgeRule[] = [];
  public totalItems = 0;
  public showNoData = false;
  public pageInfo$: BehaviorSubject<ITablePageInfo>;
  public defaultPageSize: number;

  constructor(
    private readonly _inLineControlRulesHttpService: PoliciesInLineControlRulesHttpService,
    private readonly _destroyRef: DestroyRef
  ) {
    this.defaultPageSize = this._inLineControlRulesHttpService.defaultPageSize;
    this._initializePageInfo();
    this.tableRows$ = this.inLineControlRulesCurrentPage$.pipe(
      map((items) => items.map((item) => new BehaviorSubject(item)))
    );
  }

  //Update Sorting (called from Child)
  public onSortedColumnChange(newSortColumn: SortColumn): void {
    this.sortedColumn.set(newSortColumn);
  }

  public ngOnInit(): void {
    this.fetchInLineControlRulesData();
  }

  public fetchInLineControlRulesData(): void {
    this.isLoading.set(true);
    this._inLineControlRulesHttpService.getEdgePolicyForAccount(this.integrationId()).subscribe(
      (data: EdgeRule[]) => {
        this.inLineControlRulesData = data;
        this.totalItems = this.inLineControlRulesData.length;
        const viewModels = this.inLineControlRulesData.map((item) => this._transformToViewModel(item));
        this.inLineControlRulesCurrentPage$.next(viewModels);
        this.showNoData = viewModels.length === 0;
        this.isLoading.set(false);
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (error: any) => {
        console.error('Error fetching in-line control data:', error);
        this.showNoData = true;
        this.isLoading.set(false);
        this.inLineControlRulesCurrentPage$.next([]);
      }
    );
  }

  private _transformToViewModel(row: EdgeRule): InLineControlRulesViewModel {
    return {
      rowId: row.id,
      id: row.id,
      ruleName: row.name ? row.name : '',
      ruleId: row.id,
      severity: row.severity,
      detection: this._calculateActionStatus(row, EdgeRuleAction.DETECT),
      prevention: this._calculateActionStatus(row, EdgeRuleAction.BLOCK),
      status: row.active ? StatusLabelEnum.active : StatusLabelEnum.inactive,
      clickable: false,
    };
  }

  private _calculateActionStatus(row: EdgeRule, action: EdgeRuleAction): boolean {
    if (![EdgeRuleAction.DETECT, EdgeRuleAction.BLOCK].includes(action)) {
      throw new Error(`Unsupported action for in-line control rules ${action}`);
    }

    if (!row.active) {
      return false;
    }

    return row.action == EdgeRuleAction.BLOCK || action == EdgeRuleAction.DETECT;
  }

  private _initializePageInfo() {
    this.pageInfo$ = new BehaviorSubject<ITablePageInfo>({
      number: 1,
      currSize: 1,
      maxSize: this._inLineControlRulesHttpService.defaultPageSize,
      totalItems: 1,
      totalPages: 1,
    });
  }

  public onToggleClick(event: ToggleClicked): void {
    const rowId = event.row.getValue().rowId;
    const row = this.inLineControlRulesCurrentPage$.getValue().find((row) => row.id === rowId);
    if (!row) {
      throw new Error(`Row with id ${rowId} not found`);
    }

    const edgeRule = this.inLineControlRulesData.find((rule) => rule.id === rowId);

    if (!['detection', 'prevention'].includes(event.propertyKey)) {
      throw new Error(`Unsupported property key for in-line control rules ${event.propertyKey}`);
    }

    let detection = row.detection;
    let prevention = row.prevention;
    let newValue = event.toggleState;
    let modifiedField = event.propertyKey;

    if (newValue) {
      if (modifiedField === 'detection') {
        detection = true;
        prevention = false;
      } else if (modifiedField === 'prevention') {
        detection = true;
        prevention = true;
      }
    } else {
      if (modifiedField === 'detection') {
        detection = false;
        prevention = false;
      } else if (modifiedField === 'prevention') {
        detection = true;
        prevention = false;
      }
    }

    const status = detection || prevention ? StatusLabelEnum.active : StatusLabelEnum.inactive;

    row.detection = detection;
    row.prevention = prevention;
    row.status = status;

    const rulePolicy: EdgeRulePolicy = this._calculateRulePolicy(row);

    this._inLineControlRulesHttpService
      .updateEdgeRule(edgeRule?.policyId, rowId, rulePolicy, this.integrationId())
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe();
    this.inLineControlRulesCurrentPage$.next(this.inLineControlRulesCurrentPage$.getValue());
  }
  private _calculateRulePolicy(newRow: InLineControlRulesViewModel): EdgeRulePolicy {
    return {
      active: newRow.status === StatusLabelEnum.active,
      action: newRow.prevention ? EdgeRuleAction.BLOCK : EdgeRuleAction.DETECT,
    };
  }
}
